/** The planner's pure state machine — "new route" drawing, undo/redo, and
 * selection. No React, no map, no I/O: App.tsx owns dispatching these
 * functions off user input and owns persistence (draft autosave and saved
 * writes go through plan/store.ts); this file only ever answers "given this
 * state and this input, what's the next state." That split is what makes it
 * unit-testable without a browser — see tests/plan-core.test.ts.
 *
 * Every action is its own named pure function `(state, ...args) => state`
 * rather than a single `dispatch(action)` — matches how geo.ts/store.ts
 * expose a bag of pure functions rather than a class, and lets a caller
 * invoke exactly the action it means (no string-typed action objects to
 * typo or exhaustively switch over). */

import { emptyPlan, type LonLat, type Plan, type PlanLeg, type SavedPlan, type Waypoint } from "./types";

/** The phone's undo depth — long enough to recover from a mistake, short
 * enough that a marathon drawing session doesn't grow the stack forever. */
const UNDO_CAP = 60;

export interface PlannerState {
  mode: "browse" | "draw";
  plan: Plan;
  /** The SavedPlan id being edited, or null when drawing a brand-new route
   * (start()) rather than reopening one from the Plans sheet (loadPlan()) —
   * DONE reads this to decide overwrite vs. save-as-new. */
  editingId: string | null;
  selected: number | null;
  undo: Plan[];
  redo: Plan[];
}

// --------------------------------------------------------------- entering

/** Begin drawing a brand-new route in `region`. */
export function start(region: string, now: string): PlannerState {
  return {
    mode: "draw",
    plan: emptyPlan(region, now),
    editingId: null,
    selected: null,
    undo: [],
    redo: [],
  };
}

/** Sanitizes a possibly-untrusted dayBreaks array — an old save predating
 * this feature, or a hand-edited JSON file — into something render code
 * can trust without its own bounds-checking: numbers only, deduped,
 * ascending, and each strictly interior (matches types.ts's Plan.dayBreaks
 * doc), the same invariant every mutating op in this file maintains for a
 * break it produces itself. Returns undefined rather than [] when nothing
 * survives, matching how an absent dayBreaks reads everywhere else. */
function sanitizeDayBreaks(dayBreaks: unknown, pointsLength: number): number[] | undefined {
  if (!Array.isArray(dayBreaks)) return undefined;
  const clean = Array.from(
    new Set(dayBreaks.filter((n): n is number => typeof n === "number" && Number.isInteger(n))),
  )
    .filter((n) => n > 0 && n < pointsLength - 1)
    .sort((a, b) => a - b);
  return clean.length > 0 ? clean : undefined;
}

/** Reopen a saved plan for editing — enters draw mode with its existing
 * points/legs and remembers its id so DONE overwrites it instead of saving
 * a duplicate. History starts fresh: undoing past a freshly reloaded plan
 * doesn't mean anything. dayBreaks is sanitized on the way in (see
 * sanitizeDayBreaks) so a plan saved before validation existed, or edited
 * by hand, can't crash the day-break rendering it feeds. */
export function loadPlan(saved: SavedPlan): PlannerState {
  // Peel off the SavedPlan-only fields — plan must stay a clean Plan, not
  // carry a stale stats snapshot along for the ride (stats get recomputed
  // live off the drawn geometry as it changes).
  const { id, savedAt, stats, ...plan } = saved;
  void savedAt;
  void stats;
  return {
    mode: "draw",
    plan: { ...plan, dayBreaks: sanitizeDayBreaks(plan.dayBreaks, plan.points.length) },
    editingId: id,
    selected: null,
    undo: [],
    redo: [],
  };
}

/** Back to browse. Draft persistence (saving or discarding the in-progress
 * plan) is the UI's call, not this reducer's — it just clears the
 * in-memory selection/history that only made sense while drawing. */
export function exit(state: PlannerState): PlannerState {
  return { ...state, mode: "browse", selected: null, undo: [], redo: [] };
}

// -------------------------------------------------------------- mutating

/** Every mutating action shares this tail: push the PRIOR plan onto undo
 * (capped), clear redo (a new edit invalidates whatever could've been
 * redone), and swap in the next plan. */
function commit(state: PlannerState, plan: Plan): PlannerState {
  return {
    ...state,
    plan,
    undo: [...state.undo, state.plan].slice(-UNDO_CAP),
    redo: [],
  };
}

/** Appends a point and, once there are ≥2 points, the leg that closes it —
 * legs.length === points.length - 1 holds after every call. */
export function addPoint(state: PlannerState, at: LonLat, now: string): PlannerState {
  const points = [...state.plan.points, at];
  const legs = [...state.plan.legs];
  if (points.length >= 2) legs.push({ mode: "straight", via: [] });
  return commit(state, { ...state.plan, points, legs, updatedAt: now });
}

/** Commits a point move as ONE undo step. Pair with previewPoint during a
 * drag — see previewPoint's doc comment for the full calling contract. */
export function movePoint(
  state: PlannerState,
  i: number,
  at: LonLat,
  now: string,
): PlannerState {
  if (i < 0 || i >= state.plan.points.length) return state;
  const plan: Plan = {
    ...state.plan,
    points: state.plan.points.map((p, idx) => (idx === i ? at : p)),
    legs: degradeAdjacentLegs(state.plan.legs, i),
    updatedAt: now,
  };
  return commit(state, plan);
}

/** Live-drag preview: moves points[i] the same way movePoint does, but
 * WITHOUT checkpointing history or bumping updatedAt — it isn't a real edit
 * until the drag commits.
 *
 * Calling contract (a drag = ONE undo step): capture the state at drag
 * start (`dragStart`); call `previewPoint(dragStart, i, at)` fresh on every
 * pointer-move — always against that SAME stable `dragStart`, never
 * threaded through the previous preview's return value — and render
 * whatever it returns. At drag end, call `movePoint(dragStart, i, finalAt,
 * now)` once, again against `dragStart`, to commit. Because the commit's
 * history push reads `dragStart.plan` (untouched by any preview), one undo
 * afterward lands all the way back at the pre-drag position, not one
 * mousemove short of it. */
export function previewPoint(state: PlannerState, i: number, at: LonLat): PlannerState {
  if (i < 0 || i >= state.plan.points.length) return state;
  return {
    ...state,
    plan: {
      ...state.plan,
      points: state.plan.points.map((p, idx) => (idx === i ? at : p)),
      legs: degradeAdjacentLegs(state.plan.legs, i),
    },
  };
}

/** A leg adjacent to a moved point no longer matches its solved geometry
 * (the via[] was fit to the OLD endpoint) — degrade it to straight. v1
 * never produces a snapped leg, so this is a no-op today; written
 * generally so the future snap engine doesn't need this file touched. */
function degradeAdjacentLegs(legs: PlanLeg[], movedPointIndex: number): PlanLeg[] {
  return legs.map((leg, legIdx) => {
    const touchesMovedPoint = legIdx === movedPointIndex - 1 || legIdx === movedPointIndex;
    return touchesMovedPoint && leg.mode === "snapped" ? { mode: "straight", via: [] } : leg;
  });
}

export function deletePoint(state: PlannerState, i: number, now: string): PlannerState {
  const { points, legs } = state.plan;
  if (i < 0 || i >= points.length) return state;
  const plan: Plan = {
    ...state.plan,
    points: points.filter((_, idx) => idx !== i),
    legs: repairLegsAfterDelete(legs, i, points.length),
    dayBreaks: remapDayBreaksForDelete(state.plan.dayBreaks, i, points.length),
    updatedAt: now,
  };
  return { ...commit(state, plan), selected: shiftSelectedAfterDelete(state.selected, i) };
}

/** dayBreaks remap for deletePoint(i): drop a break sitting AT i (that
 * overnight's own point is gone), shift every break past i down by one
 * (indices beyond the deletion moved down with their points), then drop
 * anything left that's no longer interior — e.g. deleting the LAST point
 * leaves an untouched break at the old second-to-last index now sitting on
 * the (new) last point; deleting the FIRST point similarly leaves a break
 * at the old second point shifted down onto the (new) first point.
 * `pointsCountBeforeDelete` matches repairLegsAfterDelete's own parameter,
 * for the same reason: the caller still has the PRE-delete points.length
 * in scope. */
function remapDayBreaksForDelete(
  dayBreaks: number[] | undefined,
  i: number,
  pointsCountBeforeDelete: number,
): number[] | undefined {
  if (!dayBreaks || dayBreaks.length === 0) return undefined;
  const newLength = pointsCountBeforeDelete - 1;
  const next = dayBreaks
    .filter((b) => b !== i)
    .map((b) => (b > i ? b - 1 : b))
    .filter((b) => b > 0 && b < newLength - 1);
  return next.length > 0 ? next : undefined;
}

/** legs[j] connects points[j] -> points[j+1]. Deleting point i:
 * - an endpoint (i===0 or i===last) just drops its one leg.
 * - a middle point fuses its two legs (i-1 and i) into ONE straight leg
 *   with via:[] — v1 has no path to solve for the new direct connection,
 *   so it degrades rather than trying to splice snapped geometry. */
function repairLegsAfterDelete(
  legs: PlanLeg[],
  i: number,
  pointsCountBeforeDelete: number,
): PlanLeg[] {
  if (i === 0) return legs.slice(1);
  if (i === pointsCountBeforeDelete - 1) return legs.slice(0, -1);
  return [...legs.slice(0, i - 1), { mode: "straight", via: [] }, ...legs.slice(i + 1)];
}

function shiftSelectedAfterDelete(
  selected: number | null,
  deletedIndex: number,
): number | null {
  if (selected === null) return null;
  if (selected === deletedIndex) return null; // the selected point is gone
  return selected > deletedIndex ? selected - 1 : selected;
}

/** Inserts a new control point ON leg `legIndex` — the leg connecting
 * points[legIndex] -> points[legIndex+1] — splitting it into two straight
 * legs; any snapped geometry the original leg had no longer matches a
 * route through the new point, so both halves start straight. selected
 * shifts the same way delete's does: point indices after the split move up
 * by one. The new point itself is NOT auto-selected — the UI selects
 * explicitly. Foundation for "click the line to add a point here" (UX
 * review's top functional gap). */
export function insertPoint(
  state: PlannerState,
  legIndex: number,
  at: LonLat,
  now: string,
): PlannerState {
  const { points, legs } = state.plan;
  if (legIndex < 0 || legIndex >= legs.length) return state;
  const plan: Plan = {
    ...state.plan,
    points: [...points.slice(0, legIndex + 1), at, ...points.slice(legIndex + 1)],
    legs: [
      ...legs.slice(0, legIndex),
      { mode: "straight", via: [] },
      { mode: "straight", via: [] },
      ...legs.slice(legIndex + 1),
    ],
    dayBreaks: remapDayBreaksForInsert(state.plan.dayBreaks, legIndex + 1),
    updatedAt: now,
  };
  const selected =
    state.selected === null || state.selected <= legIndex ? state.selected : state.selected + 1;
  return { ...commit(state, plan), selected };
}

/** dayBreaks remap for insertPoint: the new point lands at array index
 * `newPointIndex` (legIndex + 1) — every break at or past that index
 * shifts up by one to keep naming the same physical point; nothing needs
 * dropping, since shifting an already-interior index up (points.length
 * also grew by one) can never push it out of interior range. */
function remapDayBreaksForInsert(
  dayBreaks: number[] | undefined,
  newPointIndex: number,
): number[] | undefined {
  if (!dayBreaks || dayBreaks.length === 0) return undefined;
  return dayBreaks.map((b) => (b >= newPointIndex ? b + 1 : b));
}

/** 5-decimal-place match (~1.1 m at the equator) — the same rounding
 * App.tsx's attempted-set keys use for the leg solver, so "the endpoints
 * moved" and "this pair needs a fresh attempt" agree everywhere. Compared
 * as rounded strings rather than a numeric epsilon so both call sites can
 * describe the tolerance identically without sharing a magic number. */
function roughlySamePoint(a: LonLat, b: LonLat): boolean {
  return a[0].toFixed(5) === b[0].toFixed(5) && a[1].toFixed(5) === b[1].toFixed(5);
}

/** Applies the async snap-engine's result for one leg — the completion of
 * router.ts's solveLeg(a, b), fired from an App effect once snap+route
 * resolves. Deliberately NOT `commit`: this is the solver catching up to
 * an edit the user already made (addPoint, a drag), not a new edit in its
 * own right, so it pushes no undo checkpoint and leaves updatedAt alone —
 * undoing past a snap resolution should feel like it never happened.
 *
 * GUARDED against staleness: if points[legIndex]/[legIndex+1] no longer
 * match `from`/`to` (5-decimal tolerance), the geometry this solve was
 * FOR has moved on — a drag, an undo, a delete, another insert — and the
 * result is silently dropped rather than applied to whatever leg now sits
 * at that index. The effect that watches `plan` notices the new geometry
 * and re-fires on its own; nothing here needs to kick it. */
export function applyLegSolution(
  state: PlannerState,
  legIndex: number,
  from: LonLat,
  to: LonLat,
  leg: PlanLeg,
): PlannerState {
  const { points, legs } = state.plan;
  if (legIndex < 0 || legIndex >= legs.length) return state;
  if (!roughlySamePoint(points[legIndex], from) || !roughlySamePoint(points[legIndex + 1], to)) {
    return state;
  }
  return {
    ...state,
    plan: { ...state.plan, legs: legs.map((l, i) => (i === legIndex ? leg : l)) },
  };
}

/** Shared by reverse/outAndBack: a leg list run backwards — order flipped,
 * and each leg's own via[] flipped in place — so leg[i] still connects
 * whatever now sits at points[i] to points[i+1] once the caller reverses
 * (or mirrors) the point list the same way. */
function reverseLegs(legs: PlanLeg[]): PlanLeg[] {
  return [...legs].reverse().map((leg) => ({ ...leg, via: [...leg.via].reverse() }));
}

/** dayBreaks remap for reverse(): mirror each interior index the same way
 * `selected` mirrors below (i -> points.length-1-i) — dayBreaks are
 * CONTROL-POINT indices (types.ts's doc), so they transform exactly like
 * points/selected do, matching the phone's PlanScreen.reverseRoute.
 * Mirroring flips ascending order, so re-sort; the interior filter is
 * defensive (mirroring an already-interior index always yields another
 * one) — it only matters for a plan whose dayBreaks were never validated,
 * e.g. a hand-edited save that skipped loadPlan's sanitizer. */
function remapDayBreaksForReverse(
  dayBreaks: number[] | undefined,
  pointsLength: number,
): number[] | undefined {
  if (!dayBreaks || dayBreaks.length === 0) return undefined;
  const next = dayBreaks
    .map((b) => pointsLength - 1 - b)
    .filter((b) => b > 0 && b < pointsLength - 1)
    .sort((a, b) => a - b);
  return next.length > 0 ? next : undefined;
}

/** Flips the whole plan end-for-start: points reversed, legs reversed to
 * match (order and each via), modes untouched, selected AND dayBreaks
 * mirrored across the point list (see remapDayBreaksForReverse). Waypoints
 * need no remap: Waypoint.at is a coordinate, not a points[] index, so it
 * already reads correctly however the route runs. */
export function reverse(state: PlannerState, now: string): PlannerState {
  const { points, legs } = state.plan;
  const plan: Plan = {
    ...state.plan,
    points: [...points].reverse(),
    legs: reverseLegs(legs),
    dayBreaks: remapDayBreaksForReverse(state.plan.dayBreaks, points.length),
    updatedAt: now,
  };
  const selected = state.selected === null ? null : points.length - 1 - state.selected;
  return { ...commit(state, plan), selected };
}

/** There and back again: appends the mirror of the route back to its
 * start — points += every point but the last, reversed; legs += the same
 * legs reversed (order and each via) so the return trip retraces the
 * outbound geometry backwards; plus a "Turnaround" waypoint dropped at the
 * apex. The phone's exact convention — PlanScreen.outAndBack. Guards on
 * <2 points: with fewer there's no leg to mirror and no meaningful apex.
 * selected is left alone — it still names the same physical point.
 * dayBreaks is left alone too (the `...state.plan` spread below carries it
 * forward untouched): every existing break is still a valid interior
 * control-point index in the doubled route, since the point array only
 * ever grows past it — mirroring a phantom overnight onto the return leg
 * isn't something the user asked for. */
export function outAndBack(state: PlannerState, now: string): PlannerState {
  const { points, legs, waypoints } = state.plan;
  if (points.length < 2) return state;
  const turnaround = points[points.length - 1];
  const plan: Plan = {
    ...state.plan,
    points: [...points, ...points.slice(0, -1).reverse()],
    legs: [...legs, ...reverseLegs(legs)],
    waypoints: [...waypoints, { at: turnaround, name: "Turnaround" }],
    updatedAt: now,
  };
  return commit(state, plan);
}

/** Adds a waypoint at `at` named `name` — the phone's model (types.ts's
 * Waypoint doc): the KIND IS THE NAME, no separate enum. Mirrors
 * PlanScreen.swift's addMark exactly: a repeat kind gets a " 2"/" 3"...
 * suffix, counted by NAME PREFIX rather than exact match (`n =
 * marks.count(where: { $0.wp.name.hasPrefix(kind) }) + 1` on the phone) —
 * so "Fuel" then "Fuel 2" already on the plan makes the next one "Fuel 3".
 * Safe across the fixed 7-word vocabulary since none is a prefix of
 * another. No-op on a blank name: an unnamed waypoint would break the
 * "kind IS the name" contract every other waypoint here relies on. */
export function addWaypoint(
  state: PlannerState,
  at: LonLat,
  name: string,
  now: string,
): PlannerState {
  const trimmed = name.trim();
  if (trimmed === "") return state;
  const n = state.plan.waypoints.filter((w) => w.name.startsWith(trimmed)).length + 1;
  const waypoint: Waypoint = { at, name: n > 1 ? `${trimmed} ${n}` : trimmed };
  return commit(state, {
    ...state.plan,
    waypoints: [...state.plan.waypoints, waypoint],
    updatedAt: now,
  });
}

export function removeWaypoint(state: PlannerState, index: number, now: string): PlannerState {
  const { waypoints } = state.plan;
  if (index < 0 || index >= waypoints.length) return state;
  return commit(state, {
    ...state.plan,
    waypoints: waypoints.filter((_, i) => i !== index),
    updatedAt: now,
  });
}

/** Toggles an overnight marker at points[pointIndex] — valid only for
 * INTERIOR points (0 < pointIndex < points.length - 1), matching
 * types.ts's Plan.dayBreaks doc: an endpoint can't host a day break.
 * Out-of-range is a no-op, same discipline as select/movePoint. Inserts
 * sorted (dayBreaks stays ascending, the invariant every other op here
 * maintains) or removes; undoable exactly like every other mutating
 * action. */
export function toggleDayBreak(state: PlannerState, pointIndex: number, now: string): PlannerState {
  const { points, dayBreaks = [] } = state.plan;
  if (pointIndex <= 0 || pointIndex >= points.length - 1) return state;
  const next = dayBreaks.includes(pointIndex)
    ? dayBreaks.filter((i) => i !== pointIndex)
    : [...dayBreaks, pointIndex].sort((a, b) => a - b);
  return commit(state, {
    ...state.plan,
    dayBreaks: next.length > 0 ? next : undefined,
    updatedAt: now,
  });
}

// ------------------------------------------------------------ non-mutating

/** Selection never checkpoints — it isn't an edit to the plan. */
export function select(state: PlannerState, i: number | null): PlannerState {
  if (i !== null && (i < 0 || i >= state.plan.points.length)) return state;
  return { ...state, selected: i };
}

export function undo(state: PlannerState): PlannerState {
  if (state.undo.length === 0) return state;
  const plan = state.undo[state.undo.length - 1];
  return {
    ...state,
    plan,
    undo: state.undo.slice(0, -1),
    redo: [...state.redo, state.plan].slice(-UNDO_CAP),
    // The point set just changed underneath it; a stale selected index
    // would either land on the wrong point or point at nothing.
    selected: null,
  };
}

export function redo(state: PlannerState): PlannerState {
  if (state.redo.length === 0) return state;
  const plan = state.redo[state.redo.length - 1];
  return {
    ...state,
    plan,
    redo: state.redo.slice(0, -1),
    undo: [...state.undo, state.plan].slice(-UNDO_CAP),
    selected: null,
  };
}

// ------------------------------------------------------------ hit-testing

/** Local equirectangular scale for distance/projection math near latitude
 * `lat` (kx = 111320·cos(lat), ky = 110540 — the phone's constants, see
 * RouteLegs.swift's pointToSegmentMeters): plenty accurate at click
 * precision, no need for true geodesics. Shared by nearestLeg and
 * nearestOnLeg so both hit-tests use the identical approximation. */
function localScale(lat: number): { kx: number; ky: number } {
  return { kx: 111320 * Math.cos((lat * Math.PI) / 180), ky: 110540 };
}

/** Nearest point ON segment a-b to `at`, plus the distance — the shared
 * math behind nearestLeg/nearestOnLeg. Projects into the local meter
 * approximation, clamps t to the segment, then carries that same t back
 * into lon/lat by direct interpolation (valid because the local
 * projection just scales each axis by a constant, so interpolating by t
 * in either space lands on the same point). */
function projectToSegment(
  a: LonLat,
  b: LonLat,
  at: LonLat,
  kx: number,
  ky: number,
): { at: LonLat; distM: number } {
  const [px, py] = [at[0] * kx, at[1] * ky];
  const [ax, ay] = [a[0] * kx, a[1] * ky];
  const [bx, by] = [b[0] * kx, b[1] * ky];
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return { at: a, distM: Math.hypot(px - ax, py - ay) };
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  return {
    at: [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])],
    distM: Math.hypot(px - (ax + t * dx), py - (ay + t * dy)),
  };
}

/** Nearest leg to `at`, measuring point-to-segment distance over each
 * leg's FULL geometry — its endpoints plus its via[] — so a click near a
 * snapped leg's bend still hits that leg. Null when there are no legs —
 * the UI's cue to fall back to "add point" instead of "insert on line". */
export function nearestLeg(
  points: LonLat[],
  legs: PlanLeg[],
  at: LonLat,
): { legIndex: number; distM: number } | null {
  const hit = nearestOnLeg(points, legs, at);
  return hit ? { legIndex: hit.legIndex, distM: hit.distM } : null;
}

/** Same hit-test as nearestLeg, plus the actual point ON the leg to
 * insert at — the ghost point's position for click-the-line-to-insert
 * (docs/WEB_PLANNER_UX_REVIEW.md P1 #5). Null when there are no legs. */
export function nearestOnLeg(
  points: LonLat[],
  legs: PlanLeg[],
  at: LonLat,
): { legIndex: number; at: LonLat; distM: number } | null {
  if (legs.length === 0) return null;
  const { kx, ky } = localScale(at[1]);

  let best: { legIndex: number; at: LonLat; distM: number } | null = null;
  for (let i = 0; i < legs.length; i++) {
    const geom = [points[i], ...legs[i].via, points[i + 1]];
    for (let j = 0; j < geom.length - 1; j++) {
      const hit = projectToSegment(geom[j], geom[j + 1], at, kx, ky);
      if (best === null || hit.distM < best.distM) {
        best = { legIndex: i, at: hit.at, distM: hit.distM };
      }
    }
  }
  return best;
}
